import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env';

import {
  CoverageRulePricing,
  CreatePricingPayload,
  MovePricingAssignmentsPayload,
  MovePricingAssignmentsResult,
  NewPricingVersionPayload,
  NewQuoteParameterPayload,
  PricingDetail,
  PricingList,
  QuoteParameter,
  QuoteParameterList,
  UpdatePricingPayload,
} from '../../../interfaces/pricing.interface';
import { QuoteScheme } from '../../../interfaces/quote.interface';
import { PricingApiService } from './pricing-api.service';

/** Cliente de la API de Pricing y parámetros de la cotización. */
@Injectable()
export class PricingHttpService extends PricingApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.salesApiGateway}quotes`;
  private readonly pricingUrl = `${this.baseUrl}/settings/pricing`;

  list(scheme?: QuoteScheme): Observable<PricingList> {
    const params = scheme ? new HttpParams().set('filter[scheme]', scheme) : undefined;
    return this.http.get<PricingList>(this.pricingUrl, { params });
  }

  get(id: number): Observable<PricingDetail> {
    return this.http.get<PricingDetail>(`${this.pricingUrl}/${id}`);
  }

  create(body: CreatePricingPayload): Observable<PricingDetail> {
    return this.http.post<PricingDetail>(this.pricingUrl, body);
  }

  update(id: number, body: UpdatePricingPayload): Observable<PricingDetail> {
    return this.http.patch<PricingDetail>(`${this.pricingUrl}/${id}`, body);
  }

  addVersion(id: number, body: NewPricingVersionPayload): Observable<PricingDetail> {
    return this.http.post<PricingDetail>(`${this.pricingUrl}/${id}/versions`, body);
  }

  cancelVersion(id: number, version: number, reason: string): Observable<PricingDetail> {
    return this.http.delete<PricingDetail>(`${this.pricingUrl}/${id}/versions/${version}`, {
      body: { reason },
    });
  }

  moveAssignments(
    id: number,
    body: MovePricingAssignmentsPayload,
  ): Observable<MovePricingAssignmentsResult> {
    return this.http.post<MovePricingAssignmentsResult>(
      `${this.pricingUrl}/${id}/assignments/move`,
      body,
    );
  }

  assign(
    coverageId: number,
    scheme: QuoteScheme,
    pricingId: number,
    reason: string,
  ): Observable<CoverageRulePricing> {
    return this.http.put<CoverageRulePricing>(
      `${this.baseUrl}/coverage/rules/${coverageId}/pricing/${scheme}`,
      { pricing_id: pricingId, reason },
    );
  }

  unassign(coverageId: number, scheme: QuoteScheme, reason: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/coverage/rules/${coverageId}/pricing/${scheme}`, {
      body: { reason },
    });
  }

  listParameters(): Observable<QuoteParameterList> {
    return this.http.get<QuoteParameterList>(`${this.baseUrl}/settings/parameters`);
  }

  scheduleParameter(body: NewQuoteParameterPayload): Observable<QuoteParameter> {
    return this.http.post<QuoteParameter>(`${this.baseUrl}/settings/parameters`, body);
  }

  cancelParameter(id: number, reason: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/settings/parameters/${id}`, { body: { reason } });
  }
}
